<template>
  <div>
    <b-modal
      ref="modalEl"
      id="org-external-donors-import-modal"
      title="Import External Donors"
      hide-footer
      size="lg"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      @hidden="clearData"
    >
      <div class="modal-body">
        <div class="import-modal-content">
          <h2>
            {{ $t("dashboard.org_external_donors_import_modal_title", "Import External Donors") }}
          </h2>

          <p
            v-html="
              $t(
                'dashboard.org_external_donors_import_modal_description',
                'You can import external donors from a XLSX file. The file should contain the following columns: FirstName, LastName, DisplayName, Email, Phone, Country, City, ZIP ,Address1 ,State, ExternalID, Amount, Currency, TeamNames.'
              )
            "
          ></p>

          <b-form-group
            :label="$t('dashboard.org_external_donors_group_select', 'Select Existing Source')"
            label-for="group-select"
            label-size="sm"
            label-class="text-sm"
            class="mb-3"
          >
            <OrgExternalDonorGroupMultiselect v-model="selectedGroup" :disabled="!!groupName" />
          </b-form-group>

          <b-form-group
            :label="$t('dashboard.org_external_donors_group_name', 'OR Add New Source Name')"
            label-for="group-name"
            label-size="sm"
            label-class="text-sm"
            class="mb-3"
          >
            <b-form-input
              id="group-name"
              v-model="groupName"
              :state="isGroupNameValid"
              :placeholder="
                $t('dashboard.org_external_donors_group_name_placeholder', 'External Source Name')
              "
              :disabled="!!selectedGroup"
              size="sm"
            ></b-form-input>
          </b-form-group>

          <b-form-file
            v-model="file"
            :state="Boolean(file)"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
            accept=".xlsx"
          ></b-form-file>

          <b-form-checkbox v-model="ignoreInvalidPhones" class="mt-3">
            {{
              $t(
                "dashboard.org_external_donors_ignore_invalid_phones",
                "Ignore invalid phone numbers"
              )
            }}
          </b-form-checkbox>

          <b-alert v-if="skippedPhones.length" variant="warning" class="mt-3" show>
            <p class="mb-1">
              {{
                $t(
                  "dashboard.org_external_donors_skipped_phones",
                  "Import completed. Invalid phone numbers were ignored on these rows:"
                )
              }}
            </p>
            <ul class="mb-0">
              <li v-for="item in skippedPhones" :key="item.row">
                {{ $t("dashboard.org_external_donors_skipped_phone_row", "Row") }} {{ item.row }}:
                {{ item.phone }}
              </li>
            </ul>
          </b-alert>

          <b-alert v-if="errors.length" variant="danger" class="mt-3" show>
            <span v-for="(errMsg, index) in errors" :key="index">
              {{ errMsg }}
            </span>
          </b-alert>
        </div>
      </div>
      <div class="modal-footer tw-justify-between">
        <button class="bttn bttn--lg bttn--blue" @click="close">
          {{ $t(`dashboard.cancel_btn`) }}
        </button>
        <button class="bttn bttn--lg bttn--orange" @click="submit">
          {{ $t(`dashboard.import_btn`, "Import") }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import urls from "@/constants/urls";
import OrgExternalDonorGroupMultiselect from "./OrgExternalDonorGroupMultiselect.vue";
import axios from "axios";
import { defineComponent } from "vue";

export default defineComponent({
  name: "OrgExternalDonorsImportModal",
  components: {
    OrgExternalDonorGroupMultiselect,
  },
  data() {
    return {
      groupName: "",
      selectedGroup: null,
      file: null,
      errors: [],
      ignoreInvalidPhones: false,
      skippedPhones: [],
    };
  },
  computed: {
    isGroupNameValid() {
      return Boolean(this.groupName || this.selectedGroup);
    },
    orgCustomerID() {
      return localStorage.getItem("orgId");
    },
  },
  methods: {
    open() {
      this.$refs.modalEl.show();
    },
    close() {
      this.$refs.modalEl.hide();
    },
    clearData() {
      this.groupName = "";
      this.selectedGroup = null;
      this.file = null;
      this.errors = [];
      this.ignoreInvalidPhones = false;
      this.skippedPhones = [];
    },
    async submit() {
      if (this.errors.length) {
        this.errors = [];
      }
      this.skippedPhones = [];

      if (!this.groupName && !this.selectedGroup) {
        this.errors.push("Group name or group selection is required");
        return;
      }

      if (!this.file) {
        this.errors.push("File is required");
        return;
      }

      const params = {};

      if (this.selectedGroup) {
        params.group_id = this.selectedGroup.id;
      } else if (this.groupName) {
        params.group_name = this.groupName;
      }

      if (this.ignoreInvalidPhones) {
        params.ignore_invalid_phones = true;
      }

      const formData = new FormData();
      formData.append("file", this.file);

      const url = urls.orgExternalDonorsImportEXSX(this.orgCustomerID);

      try {
        const { data } = await axios.post(url, formData, { params });
        const skipped = data?.data?.skipped_phones || data?.skipped_phones || [];
        this.$emit("imported");
        if (skipped.length) {
          // keep modal open so the user can see which phones were ignored
          this.skippedPhones = skipped;
          this.file = null;
          return;
        }
        this.close();
      } catch (error) {
        this.errors = error.response?.data?.errors.map((e) => e.message) || [];
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.modal-body {
  padding: 1rem;
}

.import-modal-content {
  h2 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1rem;
  }
}

.modal-footer {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
}
</style>
